import { computed, inject, Injectable, signal } from '@angular/core';
import { AuthService } from '../core/auth/auth.service';
import { SupabaseService } from '../core/supabase.service';
import { Role } from '../models/enums';
import { Subscription } from '../models/interfaces';

@Injectable({
  providedIn: 'root'
})
export class TrialBannerService {

  private auth = inject(AuthService);
  private supabase = inject(SupabaseService);

  public isLoadingPortal = signal<boolean>(false);

  /**
   * Récupère le dernier abonnement de l'organisation courante.
   *
   * `profileRevision` est la seule source réactive ici : `auth.profile` est un
   * getter classique. Sans elle, ce service étant `providedIn: 'root'`, le
   * computed resterait figé sur la première organisation lue et le bandeau
   * afficherait l'essai de l'organisation précédente après un changement de
   * compte (la déconnexion ne recharge pas la page).
   */
  private latestSubscription = computed<Subscription | null>(() => {
    this.auth.profileRevision();
    const subscriptions = this.auth.profile?.organisation?.subscriptions || [];
    if (subscriptions.length === 0) return null;
    return [...subscriptions].sort(
      (a: Subscription, b: Subscription) =>
        new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
    )[0] as Subscription;
  });

  /**
   * Nombre de jours entiers restants avant la fin de la période d'essai.
   * Retourne null si non applicable.
   */
  public daysRemaining = computed<number | null>(() => {
    const subscription = this.latestSubscription();
    if (!subscription) return null;
    if (subscription.status !== 'trialing') return null;
    if (!subscription.trial_end_date) return null;

    const trialEnd = new Date(subscription.trial_end_date);
    const now = new Date();
    const diffMs = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  });

  /**
   * Au-delà de cette échéance, le bandeau se tait.
   *
   * Sans plafond, un essai long — deux ans, par exemple — affichait une
   * invitation à payer non masquable en haut de CHAQUE page, tous les jours. Un
   * décompte à quatre chiffres dit surtout « ce n'est pas pour maintenant », ce
   * qui affaiblit le message le jour où il compte. Le badge « Version d'essai »
   * de la sidenav continue d'indiquer le statut en attendant.
   */
  private readonly bannerThresholdDays = 5;

  /**
   * Badge « Version d'essai » de la sidenav : tout l'essai (14 jours), le bandeau ne venant
   * qu'à la fin (MOKA-361). Plafonné pour la même raison que le bandeau (essais longs).
   */
  private readonly badgeThresholdDays = 15;

  /** Incrémenté à la fermeture : relit le jour mémorisé. */
  private readonly dismissTick = signal(0);

  /**
   * Bandeau fermé aujourd'hui pour cette organisation (MOKA-361). Mémorisé dans le navigateur,
   * par organisation et par jour : il revient le lendemain. Sans stockage, il revient au
   * rechargement.
   */
  public dismissedToday = computed<boolean>(() => {
    this.dismissTick();
    this.auth.profileRevision();
    const key = this.dismissKey();
    if (!key) return false;
    try {
      return window.localStorage.getItem(key) === localDay(new Date());
    } catch {
      return false;
    }
  });

  /** Le dernier jour, le bandeau ne se ferme plus. */
  public canDismiss = computed<boolean>(() => (this.daysRemaining() ?? 0) > 1);

  public dismissForToday(): void {
    const key = this.dismissKey();
    if (!key || !this.canDismiss()) return;
    try {
      window.localStorage.setItem(key, localDay(new Date()));
    } catch {
      // Stockage indisponible : fermé pour cette page seulement.
    }
    this.dismissTick.update(v => v + 1);
    this.closedInSession.set(true);
  }

  /** Repli quand le stockage échoue : fermé jusqu'au prochain chargement. */
  private readonly closedInSession = signal(false);

  private dismissKey(): string | null {
    const orgId = this.auth.profile?.organisation_id;
    return orgId ? `mokah.trial-banner.dismissed.${orgId}` : null;
  }

  /**
   * true si l'utilisateur est SUPER_ADMIN de l'organisation ET que l'essai se
   * termine dans les cinq jours (période non expirée), sauf fermeture du jour.
   * Avant MOKA-361 : quinze jours, sans pouvoir le fermer — un rappel de paiement
   * sur chaque page dès le premier jour gâchait la découverte.
   */
  public showTrialBanner = computed<boolean>(() => {
    if (!this.auth.hasRoleOrSuperAdmin([Role.SuperAdmin])) return false;
    const days = this.daysRemaining();
    if (days === null) return false;
    if (days <= 0 || days > this.bannerThresholdDays) return false;
    if (this.canDismiss() && (this.dismissedToday() || this.closedInSession())) return false;
    return true;
  });

  /**
   * État de l'essai pour le badge de la sidenav : `null` quand il n'y a rien à
   * dire.
   *
   * Porté ici et non dans la sidenav pour deux raisons. Les seuils (badge sur
   * tout l'essai, bandeau sur les cinq derniers jours) vivent au même endroit,
   * pour rester cohérents quand on les change. Et la sidenav refaisait son propre tri
   * « dernier abonnement », troisième copie de la même logique, sans dépendre de
   * `profileRevision` : le badge restait donc figé sur l'organisation précédente
   * après un changement de compte.
   *
   * `expired` n'est pas soumis au seuil : un essai terminé se dit, quelle qu'ait
   * été sa durée.
   */
  public trialBadge = computed<'active' | 'expired' | null>(() => {
    const subscription = this.latestSubscription();
    if (!subscription || subscription.status !== 'trialing') return null;
    if (!subscription.trial_end_date) return 'expired';

    const days = this.daysRemaining();
    if (days === null) return null;
    if (days <= 0) return 'expired';
    return days <= this.badgeThresholdDays ? 'active' : null;
  });

  /**
   * true dans les deux derniers jours (bannière en mode urgence).
   */
  public isUrgent = computed<boolean>(() => {
    const days = this.daysRemaining();
    if (days === null) return false;
    return days > 0 && days <= 2;
  });

  /**
   * Appelle l'Edge Function stripe-portail-link et redirige vers le portail Stripe.
   */
  public async openStripePortal(): Promise<void> {
    if (this.isLoadingPortal()) return;

    const email = this.auth.profile?.email;
    if (!email) return;

    this.isLoadingPortal.set(true);

    try {
      const { data, error } = await this.supabase.client.functions.invoke(
        'stripe-portail-link',
        {
          body: {
            email,
            referrer: window.location.href
          }
        }
      );

      if (error) {
        console.error('[TrialBannerService] Stripe portal error:', error);
        return;
      }

      if (data?.stripe_portal_link) {
        window.location.href = data.stripe_portal_link;
      }
    } catch (err) {
      console.error('[TrialBannerService] Unexpected error:', err);
    } finally {
      this.isLoadingPortal.set(false);
    }
  }
}

/** Date locale AAAA-MM-JJ (le « jour » de la fermeture est celui de la personne). */
function localDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
